import React from 'react';
import { Globe } from 'lucide-react';
import { LANGUAGES, LANGUAGE_META, useI18n, type Language } from '../../i18n';

type Variant = 'default' | 'ghost' | 'compact';

interface LanguageToggleProps {
  /** `compact` drops the globe icon for tight toolbars. */
  variant?: Variant;
  className?: string;
}

const surface: Record<Variant, string> = {
  default: 'bg-white/70 border border-white/50 shadow-sm hover:bg-white/90',
  ghost: 'bg-white/40 border border-white/30 hover:bg-white/70',
  compact: 'bg-white/60 border border-white/40 hover:bg-white/80',
};

/**
 * Segmented EN / العربية switch. Both options stay visible so the control
 * reads the same in either direction and never hides the way back.
 */
export function LanguageToggle({ variant = 'default', className = '' }: LanguageToggleProps) {
  const { lang, setLang, t } = useI18n();

  return (
    <div
      role="group"
      aria-label={t('language.label')}
      className={`inline-flex items-center gap-1 rounded-full p-1 backdrop-blur-xl transition-all ${surface[variant]} ${className}`}
    >
      {variant !== 'compact' && (
        <Globe className="h-3.5 w-3.5 shrink-0 text-[#7367FF] ms-1.5" aria-hidden="true" />
      )}
      {LANGUAGES.map((code: Language) => {
        const isActive = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            aria-pressed={isActive}
            aria-label={t('language.switchTo', { language: t(code === 'ar' ? 'language.arabic' : 'language.english') })}
            lang={code}
            className={`rounded-full px-2.5 py-1 text-xs font-medium transition-all duration-200 active:scale-95 ${
              isActive
                ? 'bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] text-white shadow-sm'
                : 'text-[#434343]/70 hover:text-[#7367FF]'
            }`}
          >
            {code === 'en' ? 'EN' : 'ع'}
          </button>
        );
      })}
    </div>
  );
}

/**
 * Full-width variant used inside the Settings panel, where there is room for
 * the language's own name rather than a two-letter tag.
 */
export function LanguageOptions({ className = '' }: { className?: string }) {
  const { lang, setLang, t } = useI18n();

  return (
    <div role="group" aria-label={t('language.label')} className={`flex gap-2 ${className}`}>
      {LANGUAGES.map((code: Language) => {
        const meta = LANGUAGE_META[code];
        const isActive = lang === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => setLang(code)}
            aria-pressed={isActive}
            lang={code}
            dir={meta.dir}
            className={`flex-1 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all active:scale-[0.98] ${
              isActive
                ? 'border-[#7367FF] bg-[#7367FF]/10 text-[#7367FF]'
                : 'border-white/40 bg-white/60 text-[#434343] hover:bg-white/80'
            }`}
          >
            {meta.nativeLabel}
          </button>
        );
      })}
    </div>
  );
}
